import React, { useState, useRef, useEffect, useCallback } from 'react';
import { router } from '@inertiajs/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import {
  faPaperPlane,
  faUser,
  faEye,
  faEyeSlash,
  faList,
  faComments,
  faSpinner,
  faStickyNote,
  faSave,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import { ChatMessage, TopicCard } from './TherapyChatComponents';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import './TherapyShow.css'; // Import your CSS file for custom styles

// Move SidePanel out of the TherapyShow component to avoid remounting on every render
// (remounting was causing the writer input to lose focus after each character)
const SidePanel = React.memo(({ width, title, icon, iconColor, onClose, content, extraHeaderContent, className }) => (
  <div className={width}>
        <div className={`card shadow-sm h-100 d-flex flex-column ${className || ''}`}>
          <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
              <h2 className="h5 mb-0">
                  <FontAwesomeIcon icon={icon} className={`me-2 ${iconColor}`} />
                  {title}
              </h2>
              <div className="d-flex">
                  {extraHeaderContent}
                  <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={onClose}
                  >
                      <FontAwesomeIcon icon={faEyeSlash} />
                  </button>
              </div>
          </div>
          {content}
      </div>
  </div>
));

// New memoized WriterRoom component using an uncontrolled input to avoid re-renders on each keystroke
const WriterRoom = React.memo(({ writerMessages, writerChatRef, onSend, isWriterSending, characterName }) => {
  const inputRef = React.useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isWriterSending) return;
    const val = inputRef.current?.value?.trim();
    if (!val) return;
    onSend(val);
    // clear and restore focus
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  return (
    <>
      <div
        className="card-body overflow-auto flex-grow-1 p-3"
        style={{ height: 0 }}
        ref={writerChatRef}
      >
        {writerMessages.length > 0 ? (
          writerMessages.map((msg, i) => (
            <ChatMessage
              key={i}
              message={msg.message}
              role={msg.role}
              characterName={characterName}
            />
          ))
        ) : (
          <div className="text-center text-muted my-5">
            <p>No messages yet in the Writer's Room.</p>
            <p>Ask questions about character development.</p>
          </div>
        )}
      </div>

      <div className="card-footer bg-white">
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              ref={inputRef}
              type="text"
              className="form-control"
              placeholder="Ask a question as the writer..."
              // uncontrolled input: value managed by DOM to avoid re-renders
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isWriterSending}
            >
              {isWriterSending ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} />
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
});

// New memoized NotesPanel component using an uncontrolled textarea to avoid re-renders on each keystroke
const NotesPanel = React.memo(({ initialNotes = '', onSave, isSaving }) => {
  const textareaRef = React.useRef(null);

  const handleSave = (e) => {
    e && e.preventDefault && e.preventDefault();
    const val = textareaRef.current?.value ?? '';
    onSave(val);
    // keep focus after saving
    if (textareaRef.current) textareaRef.current.focus();
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100%' }}>
      <div className="card-body p-3 d-flex flex-column" style={{ height: 0, flexGrow: 1 }}>
        <textarea
          ref={textareaRef}
          defaultValue={initialNotes}
          className="form-control h-100"
          placeholder="Write your notes about character development, therapy insights, etc."
          style={{ resize: 'none', flexGrow: 1 }}
        ></textarea>
      </div>
      <div className="card-footer bg-white d-flex justify-content-end">
        <button className="btn btn-sm btn-success" onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
          ) : (
            <FontAwesomeIcon icon={faSave} className="me-1" />
          )}
          Save
        </button>
      </div>
    </div>
  );
});

// Main TherapyShow component
export default function TherapyShow({ therapy }) {
    // State for visibility toggles
    const [initialPanelState, setInitialPanelState] = useState({
      writerRoom: false,
      topics: false,
      notes: false,
      insights: false
  });
    const [tourCompleted, setTourCompleted] = useState(false);
    const [showWritersRoom, setShowWritersRoom] = useState(true);
    const [showTopics, setShowTopics] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [showInsights, setShowInsights] = useState(true);
   
    // Loading states for chats
    const [isWriterSending, setIsWriterSending] = useState(false);
    const [isSessionSending, setIsSessionSending] = useState(false);
    const [isSavingNotes, setIsSavingNotes] = useState(false);
   
    // State for chat messages
    const [writerMessage, setWriterMessage] = useState('');
    const [sessionMessage, setSessionMessage] = useState('');
    const [notes, setNotes] = useState(therapy.notes || '');
   
    // References to chat containers for scrolling
    const writerChatRef = useRef(null);
    const sessionChatRef = useRef(null);
   
    // Normalize message arrays to handle malformed data
    const normalizeMessages = (messagesData) => {
        if (!messagesData) return [];
        
        // If messagesData is already an array, return it
        if (Array.isArray(messagesData)) return messagesData;
        
        // Convert object to array
        const normalized = [];
        
        // First, check if we have a root-level message (common issue)
        if (messagesData.role && messagesData.message) {
            normalized.push({
                role: messagesData.role,
                message: messagesData.message,
                timestamp: messagesData.timestamp || new Date().toISOString()
            });
        }
        
        // Then add all numbered entries
        Object.keys(messagesData).forEach(key => {
            // Only process numeric keys or entries that have role/message
            if (!isNaN(key) && messagesData[key] && typeof messagesData[key] === 'object') {
                normalized.push(messagesData[key]);
            }
        });
        
        return normalized;
    };

    // Normalize topics to ensure it's always an array
    const normalizeTopics = (topicsData) => {
        if (!topicsData) return [];
        if (Array.isArray(topicsData)) return topicsData;
        
        // If it's a JSON string, try to parse it
        if (typeof topicsData === 'string') {
            try {
                const parsed = JSON.parse(topicsData);
                if (Array.isArray(parsed)) return parsed;
                return [];
            } catch (e) {
                return [];
            }
        }
        
        // If it's an object with keys that might be array indexes
        if (typeof topicsData === 'object') {
            const result = [];
            Object.keys(topicsData).forEach(key => {
                if (!isNaN(key) && topicsData[key]) {
                    result.push(topicsData[key]);
                }
            });
            return result;
        }
        
        return [];
    };
    
    // Normalize insights data to ensure it's always an array of strings
    const normalizeInsights = (insightsData) => {
        if (!insightsData) return [];
        if (Array.isArray(insightsData)) {
            return insightsData.map(insight => 
                typeof insight === 'string' ? insight : JSON.stringify(insight)
            );
        }
        
        // If it's a string, try to parse it as JSON
        if (typeof insightsData === 'string') {
            try {
                const parsed = JSON.parse(insightsData);
                if (Array.isArray(parsed)) {
                    return parsed.map(item => 
                        typeof item === 'string' ? item : JSON.stringify(item)
                    );
                }
                return [insightsData];
            } catch (e) {
                return [insightsData];
            }
        }
        
        // If it's an object, convert to array
        if (typeof insightsData === 'object') {
            return Object.values(insightsData).map(item => 
                typeof item === 'string' ? item : JSON.stringify(item)
            );
        }
        
        return [];
    };
    
    // Get messages from props and normalize them
    const sessionMessages = normalizeMessages(therapy.session_chat);
    const writerMessages = normalizeMessages(therapy.writer_chat);
    const topics = normalizeTopics(therapy.topics);
    const insights = normalizeInsights(therapy.insights);

    useEffect(() => {
      // Check if user has already seen the tour
      const hasSeenTour = localStorage.getItem('therapyTourComplete');
      
      if (!hasSeenTour) {
          const tour = initTour();
          tour.start();
      } else {
          setTourCompleted(true);
      }
  }, []);
  

  const initTour = () => {
    // No need to store initial panel state since we won't be changing anything
    
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shadow-md bg-white',
        scrollTo: true,
        cancelIcon: {
          enabled: true
        }
      },
      exitOnEsc: true
    });
    
    // Welcome step
    tour.addStep({
      id: 'welcome',
      text: `<h3>Welcome to the Character Therapy Tool!</h3>
            <p>This brief tour will show you how to use this interface to explore your character's psychology.</p>
            <p>The tour will explain the current visible panels and point to buttons for other features.</p>`,
      buttons: [
        {
          text: 'Skip Tour',
          action: () => {
            tour.complete();
            localStorage.setItem('therapyTourComplete', 'true');
            setTourCompleted(true);
          }
        },
        {
          text: 'Next',
          action: tour.next
        }
      ]
    });
  
    // Step 2: Therapy Session (always visible)
    tour.addStep({
      id: 'therapy-session',
      attachTo: {
        element: '.therapy-session-card',
        on: 'bottom'
      },
      text: `<h3>Therapy Session</h3>
            <p>This is where the conversation between your character and the AI therapist takes place.</p>
            <p>Use the input box at the bottom to speak as your character.</p>`,
      buttons: [
        {
          text: 'Back',
          action: tour.back
        },
        {
          text: 'Next',
          action: tour.next
        }
      ]
    });
    
    // Step 3: Toggle Buttons
    tour.addStep({
      id: 'toggle-buttons',
      attachTo: {
        element: '.toggle-buttons-container',
        on: 'bottom'
      },
      text: `<h3>Panel Controls</h3>
            <p>Use these buttons to show or hide additional panels that provide more context and tools.</p>
            <p>The rest of this tour will explain what each panel does, whether it's currently visible or not.</p>`,
      buttons: [
        {
          text: 'Back',
          action: tour.back
        },
        {
          text: 'Next',
          action: tour.next
        }
      ]
    });
    
    // Step 4: Writer's Room (show explanation only if visible, otherwise point to button)
    tour.addStep({
      id: 'writers-room',
      attachTo: {
        element: showWritersRoom ? '.writers-room-card' : 'button:contains("Writer\'s Room")',
        on: showWritersRoom ? 'left' : 'bottom'
      },
      text: `<h3>Writer's Room</h3>
            <p>Here you can ask questions about your character's development as the writer.</p>
            <p>The AI will respond with insights and suggestions to help you understand your character better.</p>
            ${!showWritersRoom ? '<p><strong>Click the Writer\'s Room button to show this panel.</strong></p>' : ''}`,
      buttons: [
        {
          text: 'Back',
          action: tour.back
        },
        {
          text: 'Next',
          action: tour.next
        }
      ]
    });
    
    // Step 5: Topics Panel (point to button if not visible)
    tour.addStep({
      id: 'topics-panel',
      attachTo: {
        element: showTopics ? '.topics-card' : '.tour-btn-topics',
        on: showTopics ? 'left' : 'bottom'
      },
      text: `<h3>Therapy Topics</h3>
            <p>Browse suggested therapeutic topics that are relevant to your character's psychology and narrative arc.</p>
            <p>Click any question to insert it into the character's input field.</p>
            ${!showTopics ? '<p><strong>Click the Topics button to show this panel.</strong></p>' : ''}`,
      buttons: [
        {
          text: 'Back',
          action: tour.back
        },
        {
          text: 'Next',
          action: tour.next
        }
      ]
    });

    // Step 6: Notes Panel (point to button if not visible)
 tour.addStep({
    id: 'notes-panel',
    attachTo: {
      element: showNotes ? '.notes-card' : '.tour-btn-notes',
      on: showNotes ? 'left' : 'bottom'
    },
    text: `<h3>Writer's Notes</h3>
          <p>Take private notes about character development insights as you observe the therapy session.</p>
          <p>Don't forget to click 'Save' to preserve your notes.</p>
          ${!showNotes ? '<p><strong>Click the Notes button to show this panel.</strong></p>' : ''}`,
    buttons: [
      {
        text: 'Back',
        action: tour.back
      },
      {
        text: 'Next',
        action: tour.next
      }
    ]
  });
    
    // Step 7: Insights Panel (show explanation or point to button)
    tour.addStep({
      id: 'insights-panel',
      attachTo: {
        element: showInsights ? '.insights-card' : 'button:contains("Insights")',
        on: showInsights ? 'left' : 'bottom'
      },
      text: `<h3>Character Insights</h3>
            <p>As the therapy session progresses, the AI will generate psychological insights about your character.</p>
            <p>These can help you develop deeper, more consistent character motivations and behaviors.</p>
            ${!showInsights ? '<p><strong>Click the Insights button to show this panel.</strong></p>' : ''}`,
      buttons: [
        {
          text: 'Back',
          action: tour.back
        },
        {
          text: 'Finish',
          action: tour.next
        }
      ]
    });
  
    // Completion step
    tour.addStep({
      id: 'complete',
      text: `<h3>You're All Set!</h3>
            <p>Now you're ready to explore your character's psychology through this therapeutic lens.</p>
            <p>Remember, this is a tool for character development - have fun with it!</p>`,
      buttons: [
        {
          text: 'Finish',
          action: () => {
            tour.complete();
            localStorage.setItem('therapyTourComplete', 'true');
            setTourCompleted(true);
          }
        }
      ]
    });
  
    return tour;
  }
  
  // Update the restartTour function as well
  const restartTour = () => {
    if (confirm("Start the guided tour? This will explain the features of the interface.")) {
      // No need to store panel state since we won't be changing it
      
      // Reset the tour completed flag
      setTourCompleted(false);
      
      // Create a new tour instance and start it
      setTimeout(() => {
        const tour = initTour();
        tour.start();
      }, 100);
    }
  };
  


    // Scroll to bottom of chat on new messages
    useEffect(() => {
        if (sessionChatRef.current) {
            sessionChatRef.current.scrollTop = sessionChatRef.current.scrollHeight;
        }
    }, [sessionMessages]);

    useEffect(() => {
        if (writerChatRef.current) {
            writerChatRef.current.scrollTop = writerChatRef.current.scrollHeight;
        }
    }, [writerMessages]);
   
    // Stable sendWriterMessage used by the uncontrolled WriterRoom input
    const sendWriterMessage = React.useCallback((message) => {
        if (!message) return;
        setIsWriterSending(true);

        router.post(`/therapies/${therapy.uuid}/messages`, {
            role: 'writer',
            message: message
        }, {
            onSuccess: () => {
                // we don't control the input value here (uncontrolled input clears itself),
                // just reset sending state
                setIsWriterSending(false);
            },
            onError: () => {
                setIsWriterSending(false);
            }
        });
    }, [therapy.uuid]);

    // Handle session chat submission
    const handleSessionSubmit = (e) => {
        e.preventDefault();
        if (!sessionMessage || isSessionSending) return;
       
        setIsSessionSending(true);
       
        router.post(`/therapies/${therapy.uuid}/messages`, {
            role: 'character',
            message: sessionMessage
        }, {
            onSuccess: () => {
                setSessionMessage('');
                setIsSessionSending(false);
            },
            onError: () => {
                setIsSessionSending(false);
            }
        });
    };

    // Stable saveNotes used by the uncontrolled NotesPanel
    const saveNotes = useCallback((newNotes) => {
        setIsSavingNotes(true);

        router.post(`/therapies/${therapy.uuid}/notes`, {
            notes: newNotes
        }, {
            onSuccess: () => {
                setNotes(newNotes);
                setIsSavingNotes(false);
            },
            onError: () => {
                setIsSavingNotes(false);
            }
        });
    }, [therapy.uuid]);

    // Keep old handler for compatibility (calls saveNotes with current state)
    const handleSaveNotes = () => {
        saveNotes(notes);
    };
    
    // Handle selecting a question from topics -- stable callback to avoid re-creation
    const handleSelectQuestion = useCallback((question) => {
        setSessionMessage(question);
    }, []);

    // Calculate column widths based on what's visible
    const getMainColWidth = () => {
        const visiblePanels = getVisiblePanelCount();
        
        if (visiblePanels === 0) return "col-12";
        if (visiblePanels === 1) return "col-md-8";
        if (visiblePanels >= 2) return "col-md-6";
        
        return "col-md-6"; // Default fallback
    };

    const getSidePanelWidth = () => {
        const visiblePanels = getVisiblePanelCount();
        
        if (visiblePanels === 1) return "col-md-4";
        if (visiblePanels === 2) return "col-md-3";
        if (visiblePanels === 3) return "col-md-2";
        if (visiblePanels >= 4) return "col-md-2";
        
        return "col-md-3"; // Default fallback
    };
    
    const getVisiblePanelCount = () => {
        return [showWritersRoom, showTopics, showNotes, showInsights].filter(Boolean).length;
    };
   // Add this SidePanel component function just before your main return statement:

// Helper function to determine side panel width based on visible panels
const getSidePanelVisibilityClass = () => {
  const visibleCount = [showWritersRoom, showTopics, showNotes, showInsights].filter(Boolean).length;
  
  if (visibleCount === 1) return "col-12";
  if (visibleCount === 2) return "col-md-6";
  if (visibleCount === 3) return "col-md-4";
  return "col-md-3"; // 4 or more panels
};

// Reusable SidePanel component to reduce repetition
// const SidePanel = ({ width, title, icon, iconColor, onClose, content, extraHeaderContent, className }) => (
//   <div className={width}>
//         <div className={`card shadow-sm h-100 d-flex flex-column ${className || ''}`}>
//           <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
//               <h2 className="h5 mb-0">
//                   <FontAwesomeIcon icon={icon} className={`me-2 ${iconColor}`} />
//                   {title}
//               </h2>
//               <div className="d-flex">
//                   {extraHeaderContent}
//                   <button
//                       className="btn btn-sm btn-outline-secondary"
//                       onClick={onClose}
//                   >
//                       <FontAwesomeIcon icon={faEyeSlash} />
//                   </button>
//               </div>
//           </div>
//           {content}
//       </div>
//   </div>
// );

return (
  <div className="container-fluid d-flex flex-column" 
  style={{ height: "calc(100vh - 150px)", paddingTop: "15px", paddingBottom: "15px" }}>
      {/* Header */}
      <div className="row mb-3">
          <div className="col-12">
              <div className="card shadow-sm">
                  <div className="card-body py-2">
                <div className="d-flex justify-content-between align-items-center">
                    {/* Left side: Title and scene info with Help button */}
                    <div className="d-flex align-items-center">
                        <div>
                            <h1 className="h4 mb-0 d-flex align-items-center">
                                Therapy for {therapy.character_name}
                                {/* {tourCompleted && ( 
                                    // <button 
                                    //     className="btn btn-sm btn-link text-secondary p-0 ms-2"
                                    //     onClick={restartTour}
                                    //     title="Restart the tour"
                                    //     style={{ marginTop: "2px" }}
                                    // >
                                    //     <FontAwesomeIcon icon={faQuestionCircle} />
                                    // </button>
                                // )} */}
                                <small className="text-muted ms-2 fw-normal">
                                    Scene {therapy.selected_scene_number}
                                </small>
                            </h1>
                        </div>
                    </div>

                      <div className="d-flex flex-wrap gap-2 toggle-buttons-container">
                          <button
                              className={`btn btn-sm ${showWritersRoom ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setShowWritersRoom(!showWritersRoom)}
                          >
                              <FontAwesomeIcon icon={showWritersRoom ? faEyeSlash : faEye} className="me-1" />
                              Writer's Room
                          </button>
                          <button
                              className={`btn btn-sm tour-btn-topics ${showTopics ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setShowTopics(!showTopics)}
                          >
                              <FontAwesomeIcon icon={showTopics ? faEyeSlash : faEye} className="me-1" />
                              Topics
                          </button>
                          <button
                              className={`btn btn-sm tour-btn-notes ${showNotes ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setShowNotes(!showNotes)}
                          >
                              <FontAwesomeIcon icon={showNotes ? faEyeSlash : faEye} className="me-1" />
                              Notes
                          </button>
                          <button
                              className={`btn btn-sm ${showInsights ? 'btn-primary' : 'btn-outline-primary'}`}
                              onClick={() => setShowInsights(!showInsights)}
                          >
                              <FontAwesomeIcon icon={showInsights ? faEyeSlash : faEye} className="me-1" />
                              Insights {insights.length > 0 && <span className="badge bg-danger ms-1">{insights.length}</span>}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>
     
      {/* Main Content with 50/50 split on medium+ screens */}
      <div className="row flex-grow-1" style={{ minHeight: 0 }}>
          {/* Main column: always takes 12 cols on small screens, 6 cols on medium+ */}
          <div className="col-12 col-md-6 d-flex flex-column mb-3 mb-md-0">
              {/* Therapy Session - Always visible */}
              <div className="card h-100 d-flex flex-column therapy-session-card border-4 border-dark shadow-lg">
              {/* <div className="card shadow-lg border-primary h-100 d-flex flex-column therapy-session-card"> */}
              {/* <div className="card shadow-sm h-100 d-flex flex-column  therapy-session-card"> */}
              <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
              {/* <div className="card-header bg-white d-flex justify-content-between align-items-center py-2"> */}
                      <h2 className="h5 mb-0">
                          <FontAwesomeIcon icon={faComments} className="me-2 text-success" />
                          Therapy Session
                      </h2>
                  </div>
                 
                  {/* Chat messages */}
                  <div
                      className="card-body overflow-auto flex-grow-1 p-3"
                      style={{ height: 0 }}
                      ref={sessionChatRef}
                  >
                      {sessionMessages.length > 0 ? (
                          sessionMessages.map((msg, i) => (
                              <ChatMessage
                                  key={i}
                                  message={msg.message}
                                  role={msg.role}
                                  characterName={therapy.character_name}
                              />
                          ))
                      ) : (
                          <div className="text-center text-muted my-5">
                              <p>No therapy session messages yet.</p>
                              <p>Start the session when you're ready.</p>
                          </div>
                      )}
                  </div>
                 
                  {/* Chat input */}
                  <div className="card-footer bg-white">
                      <form onSubmit={handleSessionSubmit}>
                          <div className="input-group">
                              <div className="input-group-prepend">
                                  <span className="input-group-text bg-warning text-dark border-warning">
                                      <FontAwesomeIcon icon={faUser} className="me-2" />
                                      {therapy.character_name}
                                  </span>
                              </div>
                              <input
                                  type="text"
                                  className="form-control"
                                  value={sessionMessage}
                                  onChange={e => setSessionMessage(e.target.value)}
                                  placeholder={`Speak as ${therapy.character_name}...`}
                              />
                              <button
                                  type="submit"
                                  className="btn btn-primary"
                                  disabled={!sessionMessage.trim() || isSessionSending}
                              >
                                  {isSessionSending ? (
                                      <FontAwesomeIcon icon={faSpinner} spin />
                                  ) : (
                                      <FontAwesomeIcon icon={faPaperPlane} />
                                  )}
                              </button>
                          </div>
                      </form>
                  </div>
              </div>
          </div>
          
          {/* Side panels container: takes the other half on medium+ screens */}
          <div className="col-12 col-md-6">
              <div className="row h-100 g-3">
                  {/* Dynamically show panels based on visibility state */}
                  {showWritersRoom && (
                      <SidePanel 
                          width={getSidePanelVisibilityClass()}
                          title="Writer's Room"
                          icon={faUser}
                          iconColor="text-primary"
                          className="writers-room-card" // Add class here
                          onClose={() => setShowWritersRoom(false)}
                          content={
                              <WriterRoom
                                writerMessages={writerMessages}
                                writerChatRef={writerChatRef}
                                onSend={sendWriterMessage}
                                isWriterSending={isWriterSending}
                                characterName={therapy.character_name}
                              />
                          }
                      />
                  )}
                  
                  {/* Topics Panel */}
                  {showTopics && (
                      <SidePanel 
                          width={getSidePanelVisibilityClass()}
                          title="Therapy Topics"
                          className="topics-card" // Add className here
                          icon={faList}
                          iconColor="text-info"
                          onClose={() => setShowTopics(false)}
                          content={
                              <div
                                  className="card-body overflow-auto p-3"
                                  style={{ height: 0, flexGrow: 1 }}
                              >
                                  {topics && topics.length > 0 ? (
                                      topics.map((topic, index) => (
                                          <TopicCard
                                              key={index}
                                              topic={topic}
                                              onSelectQuestion={handleSelectQuestion}
                                          />
                                      ))
                                  ) : (
                                      <div className="text-center text-muted my-5">
                                          <p>No therapy topics available.</p>
                                      </div>
                                  )}
                              </div>
                          }
                      />
                  )}

                  {/* Notes Panel */}
                  {showNotes && (
                      <SidePanel 
                          width={getSidePanelVisibilityClass()}
                          title="Writer's Notes"
                          className="notes-card" // Add className here
                          icon={faStickyNote}
                          iconColor="text-warning"
                          onClose={() => setShowNotes(false)}
                          content={
                              <NotesPanel
                                initialNotes={notes}
                                onSave={saveNotes}
                                isSaving={isSavingNotes}
                              />
                          }
                      />
                  )}

                  {/* Insights Panel */}
                  {showInsights && (
                      <SidePanel 
                          width={getSidePanelVisibilityClass()}
                          title="Character Insights"
                          className="insights-card" // Add className here
                          icon={faLightbulb}
                          iconColor="text-danger"
                          onClose={() => setShowInsights(false)}
                          content={
                              <div
                                  className="card-body overflow-auto p-3"
                                  style={{ height: 0, flexGrow: 1 }}
                              >
                                  {insights && insights.length > 0 ? (
                                      <div className="list-group">
                                          {insights.map((insight, index) => (
                                              <div key={index} className="list-group-item list-group-item-action mb-2">
                                                  <div className="d-flex w-100 justify-content-between">
                                                      <small className="text-muted">Insight #{index + 1}</small>
                                                  </div>
                                                  <p className="mb-1">{insight}</p>
                                              </div>
                                          ))}
                                      </div>
                                  ) : (
                                      <div className="text-center text-muted my-5">
                                          <p>No insights available yet.</p>
                                          <p>These will appear as the therapy session progresses.</p>
                                      </div>
                                  )}
                              </div>
                          }
                      />
                  )}
                  
                  {/* No panels message */}
                  {!showWritersRoom && !showTopics && !showNotes && !showInsights && (
                      <div className="col-12 h-100">
                          <div className="card shadow-sm h-100 d-flex flex-column justify-content-center align-items-center">
                              <div className="card-body text-center text-muted">
                                  <p className="mb-3">No side panels are currently displayed.</p>
                                  <p>Use the buttons above to toggle panels.</p>
                              </div>
                          </div>
                      </div>
                  )}
              </div>
          </div>
      </div>
  </div>
);
}